import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

type Voucher = Awaited<ReturnType<typeof api.operator.vouchers.list>>[number];

function getModeLabel(v: { scope: 'per_user' | 'global'; maxRedemptions: number | null }): string {
  if (v.scope === 'global') return 'Gemeinsames Budget';
  if (v.maxRedemptions === 1) return 'Einzelcode';
  return 'Pro-Nutzer-Budget';
}

export function VoucherList() {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const list = await api.operator.vouchers.list();
      setVouchers(list);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.startsWith('API 401')) {
        navigate(routes.coach.welcome(), { replace: true });
        return;
      }
      setError(msg || 'Fehler beim Laden.');
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRevoke(code: string) {
    if (!confirm(`Gutschein ${code} wirklich widerrufen?`)) return;
    try {
      await api.operator.vouchers.revoke(code);
      toast.success('Widerrufen');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    }
  }

  return (
    <AdminShell pageTitle="Vouchers">
      <PageHeader title="Gutscheine" showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        <Link to={routes.coach.operator.voucherCreate()}>
          <Button>Neuen Gutschein anlegen</Button>
        </Link>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {vouchers && vouchers.length === 0 && (
          <p className="text-sm text-muted-foreground">Noch keine Gutscheine angelegt.</p>
        )}

        {vouchers?.map((v) => (
          <Link
            key={v.code}
            to={routes.coach.operator.voucher(encodeURIComponent(v.code))}
            className="block rounded-xl border border-border px-4 py-3 space-y-2 hover:bg-muted transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-sm font-semibold">{v.code}</p>
                  {v.isStarter && (
                    <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      Starter
                    </span>
                  )}
                </div>
                {v.campaignTag && (
                  <p className="text-xs text-muted-foreground">Kampagne: {v.campaignTag}</p>
                )}
              </div>
              {v.revokedAt ? (
                <span className="text-xs text-muted-foreground italic">widerrufen</span>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRevoke(v.code); }}
                >
                  Widerrufen
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {getModeLabel(v)} · {v.redemptionCount} Einlösung(en){v.maxRedemptions != null && ` von max. ${v.maxRedemptions}`}
            </p>
            <p className="text-xs text-muted-foreground">
              {v.scope === 'global'
                ? `${v.totalUsed} von ${v.usageLimit} Nachrichten geteilt`
                : `${v.totalUsed} Nachrichten insgesamt · je ${v.usageLimit} pro Einlöser`}
              {v.expiresAt && ` · läuft ab am ${new Date(v.expiresAt).toLocaleDateString()}`}
            </p>
          </Link>
        ))}
      </div>
    </AdminShell>
  );
}
